import {
  ASSISTANT_TOOL_CONTRACTS,
  AssistantToolRenderer,
} from '@digital-twins/shared-types/assistant-tools.generated'

/**
 * How the thread shows one finished tool call, beyond the done frame.
 *
 * Only the behaviour the screen actually drives from the registry lives here
 * (today: whether a call's `tool_end` refreshes the living artifact rail). The
 * done frame's tails (`charts`, `tables`, `citations`, `preparedRun`,
 * `preparedStudy`, `socialTrackerProposal`) are read by key from the frame itself, whichever
 * tool produced them, so this map does not mirror them.
 */
export interface ThreadToolRendering {
  /** The call's `tool_end` refreshes the living artifact rail. */
  refreshesArtifactRail: boolean
}

/**
 * One entry per registry renderer. Keyed by the generated union, so a renderer
 * added in python (`models/assistant_tool_registry/_base.py`) fails this file's
 * tsc until the thread decides how it shows.
 */
export const THREAD_TOOL_RENDERINGS: Record<AssistantToolRenderer, ThreadToolRendering> = {
  none: { refreshesArtifactRail: false },
  citations: { refreshesArtifactRail: false },
  exhibits: { refreshesArtifactRail: false },
  social_tracker_card: { refreshesArtifactRail: false },
  confirm_card: { refreshesArtifactRail: false },
  image_tile: { refreshesArtifactRail: false },
  artifact_pane: { refreshesArtifactRail: true },
  study_pane: { refreshesArtifactRail: false },
}

const RENDERER_BY_TOOL: ReadonlyMap<string, AssistantToolRenderer> = new Map(
  ASSISTANT_TOOL_CONTRACTS.map((contract) => [contract.name, contract.renderer]),
)

/** The rendering of a tool by name, from its registry entry; undefined for an unknown name. */
export function threadToolRendering(tool: string): ThreadToolRendering | undefined {
  const renderer = RENDERER_BY_TOOL.get(tool)
  if (!renderer) {
    return undefined
  }
  return THREAD_TOOL_RENDERINGS[renderer]
}
